import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Accounts.module.css";
import fetchAcTokenData from "../../APIsControll/fetchAcTokenData";
import { AddDealerIcon, LogInBtnIcon } from "../../components/icons/Icon";
import Button from "../../components/updatedComponents/Dealers/Button";
import SearchBar from "../../components/updatedComponents/FormComponents/SearchBar";
import AddUpdateAccount from "../../components/updatedComponents/Settings/Addupdateaccount";
import AccountsOtpVerify from "../../components/updatedComponents/Settings/AccountsOtpVerify";
import { ModalWrapper } from "../../exports";

const Accounts = ({
  mode,
  managerId,
  deviceId,
  iPInfo,
  browserName,
  operatinySystem,
  otpActoken,
  setOtpAcToken,
  show,
}) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  const OTP_LENGTH = 6;
  const [adminOtp, setAdminOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [accountsOtp, setAccountsOtp] = useState(Array(OTP_LENGTH).fill(""));

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const pendingPayloadRef = useRef(null);

  // ─── Fetch accounts list ───────────────────────────────────────────────────
  const getAccountsData = async () => {
    try {
      setLoading(true);

      const payload = {
        managerId,
        deviceId,
        // ip: iPInfo?.ip || "192.168.1.6",
        // browser: browserName || "chrome",
        // os: operatinySystem || "window",
        // city: iPInfo?.city || "lucknow",
        // state: iPInfo?.region || "uttar pradesh",
        // country: iPInfo?.country_name || "india",
        ip: iPInfo?.ip,
        browser: browserName,
        os: operatinySystem,
        city: iPInfo?.city,
        state: iPInfo?.region,
        country: iPInfo?.country_name,
      };

      const result = await fetchAcTokenData(
        "admin/accounts",
        payload,
        otpActoken,
      );

      if (result?.status === "success") {
        setOtpAcToken(result?.actoken);
        setAccounts(result?.accounts);
      } else {
        show(result?.msg || "Failed to load accounts");
      }
    } catch (error) {
      console.error("getAccountsData error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAccountsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId, iPInfo, deviceId, browserName, operatinySystem]);

  // ─── Filter accounts by search term ───────────────────────────────────────
  const filteredAccounts = useMemo(() => {
    return accounts.filter((item) => {
      const keyword = search.toLowerCase();
      return (
        item.bankName?.toLowerCase().includes(keyword) ||
        item.acHolderName?.toLowerCase().includes(keyword) ||
        item.branchName?.toLowerCase().includes(keyword) ||
        item.ifscCode?.toLowerCase().includes(keyword) ||
        String(item.accountNumber).includes(keyword)
      );
    });
  }, [search, accounts]);

  // ─── Build FormData from a payload object ─────────────────────────────────
  const buildFormData = (payload, { includeOtp = false } = {}) => {
    const formData = new FormData();

    formData.append("deviceId", deviceId);
    formData.append("acHolderName", payload.acHolderName);
    formData.append("bankName", payload.bankName);
    formData.append("branchName", payload.branchName);
    formData.append("ifscCode", payload.ifscCode);
    formData.append("accountNumber", payload.accountNumber);
    formData.append("upi", payload.upi);
    formData.append("cart", payload.cart);
    formData.append("pcart", payload.pcart);
    formData.append("webCollection", payload.webCollection);
    formData.append("apiCollection", payload.apiCollection);
    formData.append("status", payload.status);

    // formData.append("ip", iPInfo?.ip || "192.168.1.6");
    // formData.append("browser", browserName || "chrome");
    // formData.append("os", operatinySystem || "window");
    // formData.append("city", iPInfo?.city || "lucknow");
    // formData.append("state", iPInfo?.region || "uttar pradesh");
    // formData.append("country", iPInfo?.country_name || "india");

    formData.append("ip", iPInfo?.ip);
    formData.append("browser", browserName);
    formData.append("os", operatinySystem);
    formData.append("city", iPInfo?.city);
    formData.append("state", iPInfo?.region);
    formData.append("country", iPInfo?.country_name);

    if (payload.qrcode) {
      formData.append("qrcode", payload.qrcode);
    }

    // OTPs read at call-time from current state — always fresh
    if (includeOtp) {
      formData.append("adminEmailOtp", adminOtp.join(""));
      formData.append("emailOtp", accountsOtp.join(""));
    }

    return formData;
  };

  // ─── STEP 1: Send OTP — called by AddUpdateAccount on "Next" ──────────────
  const sendOtp = async (payload, isUpdate) => {
    pendingPayloadRef.current = { payload, isUpdate };

    try {
      const formData = buildFormData(payload, { includeOtp: false });

      const acIdParam = isUpdate && payload._id ? `?acId=${payload._id}` : "";

      const response = await fetch(
        `${process.env.REACT_APP_URL}admin/addUpdateAc/${managerId}${acIdParam}`,
        {
          method: "POST",
          headers: {
            "wa-platform": process.env.REACT_APP_ADMIN_SECRET,
            actoken: otpActoken,
          },
          body: formData,
        },
      );

      const res = await response.json();

      if (res.status === "success") {
        if (res.actoken) setOtpAcToken(res.actoken);
        show(res?.msg);
        // Reset OTP inputs, then open the modal
        setAdminOtp(Array(OTP_LENGTH).fill(""));
        setAccountsOtp(Array(OTP_LENGTH).fill(""));
        setIsOtpModalOpen(true);
      } else {
        show(res.msg || "Failed to send OTP");
      }
    } catch (err) {
      console.error("sendOtp error:", err);
      show("Something went wrong while sending OTP");
    }
  };

  const createAccountsData = async () => {
    if (adminOtp.some((d) => d === "")) {
      show("Please enter the Admin OTP");
      return;
    }
    if (accountsOtp.some((d) => d === "")) {
      show("Please enter the Profile OTP");
      return;
    }

    const saved = pendingPayloadRef.current;
    if (!saved) {
      show("Session expired, please try again");
      return;
    }

    setLoading(true);
    try {
      const formData = buildFormData(saved.payload, { includeOtp: true });

      const acIdParam =
        saved.isUpdate && saved.payload._id ? `?acId=${saved.payload._id}` : "";

      const response = await fetch(
        `${process.env.REACT_APP_URL}admin/addUpdateAc/${managerId}${acIdParam}`,
        {
          method: "POST",
          headers: {
            "wa-platform": process.env.REACT_APP_ADMIN_SECRET,
            actoken: otpActoken,
          },
          body: formData,
        },
      );

      const res = await response.json();

      if (res.status === "success") {
        setOtpAcToken(res.actoken);
        show(res.msg);
        setIsOtpModalOpen(false);
        setModalOpen(false);
        pendingPayloadRef.current = null;
        getAccountsData();
      } else {
        show(res.msg || "OTP verification failed");
      }
    } catch (err) {
      console.error("createSettingsData error:", err);
      show("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOtpModal = () => {
    setIsOtpModalOpen(false);
  };

  return (
    <div className={`${styles.root} ${mode ? styles.dark : styles.light}`}>
      <ModalWrapper
        isOpen={isOtpModalOpen}
        onClose={handleCloseOtpModal}
        flag="alwaysopen"
      >
        <AccountsOtpVerify
          mode={mode}
          createAccountsData={createAccountsData}
          adminOtp={adminOtp}
          setAdminOtp={setAdminOtp}
          accountsOtp={accountsOtp}
          setAccountsOtp={setAccountsOtp}
          loading={loading}
          onClose={handleCloseOtpModal}
        />
      </ModalWrapper>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.search_bar}>
          <SearchBar
            type="text"
            padding={"6px"}
            fontSize={"12px"}
            fontWeight={"400"}
            borderRadius={"10px"}
            placeholder={"Search account, bank, IFSC..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.actions}>
          <Button
            buttonText="Add Account"
            buttonBgColor="#4164E3"
            buttonTextColor="#fff"
            buttonClick={() => {
              setEditAccount(null);
              setModalOpen(true);
            }}
            buttonIcon={<AddDealerIcon />}
          />

          {/* <Button
            buttonText="Logout"
            buttonBgColor="#000"
            buttonTextColor="#fff"
            buttonIcon={<LogInBtnIcon />}
          /> */}
        </div>
      </div>

      {/* Account Cards */}
      {loading ? (
        <div className={styles.loaderWrapper}>
          <div className={styles.loader}></div>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {filteredAccounts.length > 0 ? (
            filteredAccounts.map((item) => (
              <div
                key={item._id}
                className={styles.card}
                onClick={() => {
                  setEditAccount(item);
                  setModalOpen(true);
                }}
              >
                {/* <div className={styles.menu}>⋮</div> */}

                <div className={styles.imageWrapper}>
                  {item.qrcode ? (
                    <img
                      src={process.env.REACT_APP_S3URL + item.qrcode}
                      alt={item.bankName}
                      className={styles.qrImage}
                    />
                  ) : (
                    <div className={styles.noQr}>No QR</div>
                  )}
                </div>

                <h3 title={item.bankName}>{item.bankName}</h3>

                <p className={styles.accountHolder}>{item.acHolderName}</p>

                <p>
                  <strong>Branch:</strong> {item.branchName}
                </p>

                <p>
                  <strong>IFSC:</strong> {item.ifscCode}
                </p>

                <p>
                  <strong>A/C:</strong> {item.accountNumber}
                </p>

                <span
                  className={`${styles.status} ${
                    item.status === "Active" ? styles.active : styles.inactive
                  }`}
                >
                  {item.status}
                </span>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>No accounts found</div>
          )}
        </div>
      )}

      {/* Add / Update Account Drawer */}
      <AddUpdateAccount
        mode={mode}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editData={editAccount}
        onSendOtp={sendOtp}
      />
    </div>
  );
};

export default Accounts;
